import { useEffect, useState } from "react";
import {
  adminCreateUser,
  adminListUsers,
  adminUpdateUser,
  getSystemConfig,
  updateSystemConfig,
  adminListModels,
  adminCreateModel,
  adminUpdateModel,
  adminDeleteModel,
  adminTestModel,
  adminListMcpServers,
  adminCreateMcpServer,
  adminUpdateMcpServer,
  adminDeleteMcpServer,
  adminTestMcpServer,
  adminListResearchBackends,
  adminCreateResearchBackend,
  adminUpdateResearchBackend,
  adminDeleteResearchBackend,
  adminTestResearchBackend,
  adminListV2Parameters,
  adminListV2ParameterValues,
  adminUpdateV2Parameter,
  adminListPromptTemplates,
  adminGetPromptTemplateHistory,
  adminUpdatePromptTemplate,
  adminRevertPromptTemplate,
} from "../transport/authClient";
import type {
  AdminUserListItem,
  CreateUserPayload,
  SystemConfig,
  LlmModel,
  ModelTestResult,
  McpServer,
  McpServerPayload,
  McpTestResult,
  ResearchBackend,
  ResearchBackendCreatePayload,
  ResearchBackendPatchPayload,
  ResearchBackendTestResult,
  V2ParameterCatalogItem,
  V2ParameterValue,
  PromptTemplate,
  PromptTemplateHistoryEntry,
} from "../transport/authClient";
import { AppShell } from "../components/layout/AppShell";
import { CatalogsTab } from "../components/admin/CatalogsTab";
import { InfrastructureTab } from "../components/admin/InfrastructureTab";
import { CorpusTab } from "../components/admin/CorpusTab";

type Tab =
  | "users"
  | "system"
  | "models"
  | "mcp"
  | "research"
  | "parameters"
  | "prompts"
  | "catalogs"
  | "infrastructure"
  | "corpus";


// P2 — VoxCPM2/Chatterbox (GPU, on-prem) ve ElevenLabs (cloud) motorları
// tamamen kaldırıldı (kullanıcı talebi: "tts kaldır hacım edgede kalsın
// sistem"). Tek motor: native tr-TR Edge-TTS.
const TTS_ENGINE_OPTIONS = [
  { id: "edge-tts", label: "Edge-TTS", desc: "Microsoft Neural, native tr-TR" },
  { id: "off", label: "Kapalı", desc: "TTS devre dışı" },
];

const MODEL_FAMILY_OPTIONS = [
  { id: "dai-core", label: "DAI Çekirdek (Gemma)" },
  { id: "dai-reasoning", label: "DAI Muhakeme (DeepSeek/Qwen)" },
  { id: "dai-vision", label: "DAI Görme (Çoklu-Modlu)" },
  { id: "openai", label: "OpenAI Uyumlu" },
  { id: "anthropic", label: "Anthropic Uyumlu" },
  { id: "custom", label: "Özel" },
];

export function AdminPage() {
  const [tab, setTab] = useState<Tab>("users");

  return (
    <AppShell>
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-white/[0.03] border border-white/5 rounded-xl p-1 mb-6 w-fit">
          <TabBtn active={tab === "users"} onClick={() => setTab("users")}>
            Kullanıcı Yönetimi
          </TabBtn>
          <TabBtn active={tab === "models"} onClick={() => setTab("models")}>
            LLM Modelleri
          </TabBtn>
          <TabBtn active={tab === "mcp"} onClick={() => setTab("mcp")}>
            MCP Sunucuları
          </TabBtn>
          <TabBtn active={tab === "research"} onClick={() => setTab("research")}>
            Web Araştırma Kaynakları
          </TabBtn>
          <TabBtn active={tab === "system"} onClick={() => setTab("system")}>
            Sistem Ayarları
          </TabBtn>
          <TabBtn active={tab === "parameters"} onClick={() => setTab("parameters")}>
            v2 Parametreleri
          </TabBtn>
          <TabBtn active={tab === "prompts"} onClick={() => setTab("prompts")}>
            Prompt Şablonları
          </TabBtn>
          <TabBtn active={tab === "catalogs"} onClick={() => setTab("catalogs")}>
            Kataloglar
          </TabBtn>
          <TabBtn active={tab === "infrastructure"} onClick={() => setTab("infrastructure")}>
            Altyapı
          </TabBtn>
          <TabBtn active={tab === "corpus"} onClick={() => setTab("corpus")}>
            Kanıt Havuzu
          </TabBtn>
        </div>

        {tab === "users" && <UsersTab />}
        {tab === "models" && <ModelsTab />}
        {tab === "mcp" && <McpServersTab />}
        {tab === "research" && <ResearchBackendsTab />}
        {tab === "system" && <SystemConfigTab />}
        {tab === "parameters" && <V2ParametersTab />}
        {tab === "prompts" && <PromptTemplatesTab />}
        {tab === "catalogs" && <CatalogsTab />}
        {tab === "infrastructure" && <InfrastructureTab />}
        {tab === "corpus" && <CorpusTab />}
      </main>
    </AppShell>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
        active
          ? "bg-indigo-600/20 text-indigo-300 border border-indigo-500/30"
          : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// v2 PARAMETERS TAB
// ═══════════════════════════════════════════════════════════════════════════════

function V2ParametersTab() {
  const [catalog, setCatalog] = useState<V2ParameterCatalogItem[]>([]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [catalogRows, valueRows] = await Promise.all([
        adminListV2Parameters(),
        adminListV2ParameterValues(),
      ]);
      setCatalog(catalogRows);
      setDrafts(
        Object.fromEntries(
          catalogRows.map((row) => [
            row.key,
            JSON.stringify(currentValue(row, valueRows), null, 2),
          ]),
        ),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Parametreler yüklenemedi");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(row: V2ParameterCatalogItem) {
    setSavingKey(row.key);
    try {
      const parsed = JSON.parse(drafts[row.key] ?? "{}") as Record<string, unknown>;
      await adminUpdateV2Parameter(row.key, {
        scope_type: "system",
        scope_id: "global",
        value: parsed,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Parametre kaydedilemedi");
    } finally {
      setSavingKey(null);
    }
  }

  const grouped = catalog.reduce<Record<string, V2ParameterCatalogItem[]>>((acc, row) => {
    (acc[row.category] ??= []).push(row);
    return acc;
  }, {});

  return (
    <section className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-indigo-300">
          Configuration as Data
        </div>
        <h2 className="mt-1 text-xl font-bold text-white">v2 Parametre Kataloğu</h2>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          v2 davranışları kod içi sabitlerden değil, doğrulanan ve auditlenen
          parametrelerden beslenecek. Bu yüzey şimdilik system/global override
          kapsamını yönetir; tenant/debate/operator scope sonraki fazda açılır.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {Object.entries(grouped).map(([category, rows]) => (
        <div key={category} className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]">
          <div className="border-b border-white/5 px-5 py-3">
            <h3 className="font-semibold text-white">{category}</h3>
          </div>
          <div className="divide-y divide-white/5">
            {rows.map((row) => (
              <div key={row.key} className="grid gap-4 px-5 py-4 lg:grid-cols-[1.2fr_1fr_auto]">
                <div>
                  <div className="font-mono text-sm text-indigo-200">{row.key}</div>
                  <div className="mt-1 text-xs text-slate-400">{row.description}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                    <span>type={row.value_type}</span>
                    <span>visibility={row.visibility}</span>
                    {row.min_value !== null && <span>min={row.min_value}</span>}
                    {row.max_value !== null && <span>max={row.max_value}</span>}
                  </div>
                </div>
                <textarea
                  value={drafts[row.key] ?? ""}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 outline-none focus:border-indigo-500/60"
                  spellCheck={false}
                />
                <button
                  onClick={() => save(row)}
                  disabled={savingKey === row.key}
                  className="h-10 self-start rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {savingKey === row.key ? "Kaydediliyor" : "Kaydet"}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function currentValue(row: V2ParameterCatalogItem, values: V2ParameterValue[]) {
  const override = values.find(
    (value) =>
      value.key === row.key &&
      value.scope_type === "system" &&
      value.scope_id === "global",
  );
  return override?.value ?? row.default_value;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROMPT TEMPLATES TAB — İş Kolu F. Her LLM sistem promptu burada admin
// tarafından düzenlenebilir; kod içindeki metin SADECE gün-1 seed +
// DB erişilemezse güvenlik-ağı fallback'idir (bkz. prompt_defaults.py).
// ═══════════════════════════════════════════════════════════════════════════════

function PromptTemplatesTab() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [history, setHistory] = useState<PromptTemplateHistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    try {
      const rows = await adminListPromptTemplates();
      setTemplates(rows);
      setDrafts(Object.fromEntries(rows.map((r) => [r.key, r.body])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Prompt şablonları yüklenemedi");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(key: string) {
    setSavingKey(key);
    setNotice(null);
    try {
      await adminUpdatePromptTemplate(key, drafts[key] ?? "");
      await load();
      setNotice(`'${key}' kaydedildi — canlı debate'lerde ANINDA etkili (restart gerekmez).`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally {
      setSavingKey(null);
    }
  }

  async function revert(key: string) {
    setSavingKey(key);
    setNotice(null);
    try {
      await adminRevertPromptTemplate(key);
      await load();
      setNotice(`'${key}' kod varsayılanına döndürüldü.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Varsayılana döndürülemedi");
    } finally {
      setSavingKey(null);
    }
  }

  async function showHistory(key: string) {
    setHistoryKey(key);
    try {
      setHistory(await adminGetPromptTemplateHistory(key));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Geçmiş yüklenemedi");
    }
  }

  const grouped = templates.reduce<Record<string, PromptTemplate[]>>((acc, row) => {
    (acc[row.category] ??= []).push(row);
    return acc;
  }, {});

  return (
    <section className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-indigo-300">
          Sıfır Hard-Code Prompt
        </div>
        <h2 className="mt-1 text-xl font-bold text-white">Prompt Şablonları</h2>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          Konsey moderatörü, topoloji direktifleri, kanıt-dürüstlüğü kuralı ve
          Hızlı Başlat fallback personaları dahil — her LLM sistem promptu
          burada. Değişiklik anında (restart'sız) canlı debate'lere yansır.
          "Varsayılana Döndür" kod içindeki güvenlik-ağı metnini yeni bir
          sürüm olarak geri yükler.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          {notice}
        </div>
      )}

      {Object.entries(grouped).map(([category, rows]) => (
        <div key={category} className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]">
          <div className="border-b border-white/5 px-5 py-3">
            <h3 className="font-semibold text-white capitalize">{category}</h3>
          </div>
          <div className="divide-y divide-white/5">
            {rows.map((row) => (
              <div key={row.key} className="space-y-3 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-sm text-indigo-200">{row.key}</div>
                    <div className="mt-1 text-xs text-slate-400">{row.description}</div>
                    <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      <span>v{row.version}</span>
                      {row.is_customized ? (
                        <span className="text-amber-400">● Admin tarafından özelleştirilmiş</span>
                      ) : (
                        <span className="text-slate-500">○ Kod varsayılanı</span>
                      )}
                      {row.updated_by && <span>son değiştiren: {row.updated_by}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => showHistory(row.key)}
                      className="h-8 rounded-lg border border-white/10 px-3 text-xs text-slate-300 hover:bg-white/5"
                    >
                      Geçmiş
                    </button>
                    {row.has_default && (
                      <button
                        onClick={() => revert(row.key)}
                        disabled={savingKey === row.key}
                        className="h-8 rounded-lg border border-white/10 px-3 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
                      >
                        Varsayılana Döndür
                      </button>
                    )}
                    <button
                      onClick={() => save(row.key)}
                      disabled={savingKey === row.key || drafts[row.key] === row.body}
                      className="h-8 rounded-lg bg-indigo-600 px-3 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {savingKey === row.key ? "Kaydediliyor…" : "Kaydet"}
                    </button>
                  </div>
                </div>
                <textarea
                  value={drafts[row.key] ?? ""}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  rows={8}
                  className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 font-mono text-xs leading-relaxed text-slate-200 outline-none focus:border-indigo-500/60"
                  spellCheck={false}
                />
                {historyKey === row.key && (
                  <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                    <div className="mb-2 flex items-center justify-between text-xs text-slate-400">
                      <span>Sürüm geçmişi</span>
                      <button onClick={() => setHistoryKey(null)} className="text-slate-500 hover:text-slate-300">
                        Kapat
                      </button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-auto">
                      {history.map((h) => (
                        <div key={h.version} className="rounded border border-white/5 p-2 text-[11px]">
                          <div className="flex justify-between text-slate-500">
                            <span>
                              v{h.version} {h.is_active && <span className="text-emerald-400">(aktif)</span>}
                            </span>
                            <span>{h.updated_by ?? "system-seed"} · {new Date(h.updated_at).toLocaleString("tr-TR")}</span>
                          </div>
                          <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-slate-400">{h.body}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// USERS TAB
// ═══════════════════════════════════════════════════════════════════════════════

function UsersTab() {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadUsers() {
    setLoading(true);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function handleToggleActive(u: AdminUserListItem) {
    try {
      await adminUpdateUser(u.id, { is_active: !u.is_active });
      await loadUsers();
    } catch (e) {
      setError(e instanceof Error ? e.message : "İşlem başarısız");
    }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Kullanıcı Yönetimi</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium transition-colors"
        >
          + Kullanıcı Ekle
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {showCreate && (
        <CreateUserForm
          onCreated={() => {
            setShowCreate(false);
            loadUsers();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-6 py-3 text-xs text-slate-500 uppercase font-medium">Kullanıcı</th>
              <th className="text-left px-6 py-3 text-xs text-slate-500 uppercase font-medium">Rol</th>
              <th className="text-left px-6 py-3 text-xs text-slate-500 uppercase font-medium">Durum</th>
              <th className="text-left px-6 py-3 text-xs text-slate-500 uppercase font-medium">Son Giriş</th>
              <th className="text-right px-6 py-3 text-xs text-slate-500 uppercase font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">Yükleniyor…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">Kullanıcı bulunamadı</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-3">
                    <p className="font-medium">{u.display_name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                      u.role === "admin" ? "bg-amber-500/10 text-amber-400" : "bg-slate-500/10 text-slate-400"
                    }`}>
                      {u.role === "admin" ? "Admin" : "Kullanıcı"}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`inline-block w-2 h-2 rounded-full ${u.is_active ? "bg-emerald-400" : "bg-red-400"}`} />
                    <span className="ml-2 text-xs text-slate-400">{u.is_active ? "Aktif" : "Pasif"}</span>
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => handleToggleActive(u)}
                      className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors"
                    >
                      {u.is_active ? "Devre Dışı" : "Aktifleştir"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM CONFIG TAB
// ═══════════════════════════════════════════════════════════════════════════════

function SystemConfigTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dynamic model list from DB
  const [modelOptions, setModelOptions] = useState<LlmModel[]>([]);

  // Local editable state
  const [llmModel, setLlmModel] = useState("brain-31b");
  const [temperature, setTemperature] = useState(0.55);
  const [topP, setTopP] = useState(0.92);
  const [maxTokens, setMaxTokens] = useState(1100);
  const [presencePenalty, setPresencePenalty] = useState(0.0);
  const [frequencyPenalty, setFrequencyPenalty] = useState(0.0);

  // Subsystem model overrides — empty string ⇒ "use default model"
  // (the sentinel maps to NULL on the wire and the backend falls
  // back to models_catalog default).
  const [commentaryModelId, setCommentaryModelId] = useState<string>("");
  const [comparisonModelId, setComparisonModelId] = useState<string>("");

  // Placeholder defaults below only render for the instant before
  // getSystemConfig() resolves (or if that call fails) — real values come
  // from the DB `system_config` row. P2 — single engine (Edge-TTS), no
  // GPU/cloud address/model/timeout knobs left to default.
  const [ttsEngine, setTtsEngine] = useState<string>("edge-tts");
  const [ttsSpeed, setTtsSpeed] = useState(1.0);

  // İş Kolu D1 — Brave Search API key. Holds the masked "••••1234"
  // display value on load; only sent back to the server if the admin
  // actually typed a new value (see handleSave below).
  const [braveApiKey, setBraveApiKey] = useState("");
  const [braveApiKeyDirty, setBraveApiKeyDirty] = useState(false);

  // İstihbarat Ajanı Faz 0 — Google Programmable Search + Yandex XML API.
  // Exact same masked-display / dirty-flag contract as Brave above.
  const [googleCseKey, setGoogleCseKey] = useState("");
  const [googleCseKeyDirty, setGoogleCseKeyDirty] = useState(false);
  const [googleCseCx, setGoogleCseCx] = useState("");
  const [googleCseCxDirty, setGoogleCseCxDirty] = useState(false);
  const [yandexApiKey, setYandexApiKey] = useState("");
  const [yandexApiKeyDirty, setYandexApiKeyDirty] = useState(false);
  const [yandexUser, setYandexUser] = useState("");
  const [yandexUserDirty, setYandexUserDirty] = useState(false);

  // İstihbarat Ajanı Faz 5 — capability feature-flags + budgets.
  const [intelVisionEnabled, setIntelVisionEnabled] = useState(false);
  const [intelEntityFollowEnabled, setIntelEntityFollowEnabled] = useState(true);
  const [intelDossierEnabled, setIntelDossierEnabled] = useState(true);
  const [intelScreenshotEnabled, setIntelScreenshotEnabled] = useState(false);
  const [intelPdfReaderEnabled, setIntelPdfReaderEnabled] = useState(true);
  const [intelMaxImagesPerTurn, setIntelMaxImagesPerTurn] = useState(3);
  const [intelMaxEntityFollowDepth, setIntelMaxEntityFollowDepth] = useState(1);

  // 2026-07-25 ayar denetimi — \parameter_catalog'tan taşınan tur bütçeleri.
  // Orada düzenlenebiliyorlardı ama motor o katmanı hiç okumuyordu; artık
  // \system_config\ üzerinden gerçekten etki ediyorlar.
  const [defaultMaxRounds, setDefaultMaxRounds] = useState(5);
  const [hardCapRounds, setHardCapRounds] = useState(50);
  const [cheapModel, setCheapModel] = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const [cfg, mdls] = await Promise.all([getSystemConfig(), adminListModels()]);
        setModelOptions(mdls.filter((m) => m.is_active));
        if (cfg.llm_default_model) setLlmModel(cfg.llm_default_model);
        if (cfg.llm_temperature != null) setTemperature(cfg.llm_temperature);
        if (cfg.llm_top_p != null) setTopP(cfg.llm_top_p);
        if (cfg.llm_max_tokens != null) setMaxTokens(cfg.llm_max_tokens);
        if (cfg.llm_presence_penalty != null) setPresencePenalty(cfg.llm_presence_penalty);
        if (cfg.llm_frequency_penalty != null) setFrequencyPenalty(cfg.llm_frequency_penalty);
        if (cfg.commentary_model_id) setCommentaryModelId(cfg.commentary_model_id);
        if (cfg.comparison_model_id) setComparisonModelId(cfg.comparison_model_id);
        if (cfg.tts_engine) setTtsEngine(cfg.tts_engine);
        if (cfg.default_max_rounds != null) setDefaultMaxRounds(cfg.default_max_rounds);
        if (cfg.hard_cap_rounds != null) setHardCapRounds(cfg.hard_cap_rounds);
        if (cfg.cheap_model) setCheapModel(cfg.cheap_model);
        if (cfg.tts_speed != null) setTtsSpeed(cfg.tts_speed);
        if (cfg.research_brave_api_key) setBraveApiKey(cfg.research_brave_api_key);
        if (cfg.research_google_cse_key) setGoogleCseKey(cfg.research_google_cse_key);
        if (cfg.research_google_cse_cx) setGoogleCseCx(cfg.research_google_cse_cx);
        if (cfg.research_yandex_api_key) setYandexApiKey(cfg.research_yandex_api_key);
        if (cfg.research_yandex_user) setYandexUser(cfg.research_yandex_user);
        if (cfg.intel_vision_enabled != null) setIntelVisionEnabled(cfg.intel_vision_enabled);
        if (cfg.intel_entity_follow_enabled != null) setIntelEntityFollowEnabled(cfg.intel_entity_follow_enabled);
        if (cfg.intel_dossier_enabled != null) setIntelDossierEnabled(cfg.intel_dossier_enabled);
        if (cfg.intel_screenshot_enabled != null) setIntelScreenshotEnabled(cfg.intel_screenshot_enabled);
        if (cfg.intel_pdf_reader_enabled != null) setIntelPdfReaderEnabled(cfg.intel_pdf_reader_enabled);
        if (cfg.intel_max_images_per_turn != null) setIntelMaxImagesPerTurn(cfg.intel_max_images_per_turn);
        if (cfg.intel_max_entity_follow_depth != null) setIntelMaxEntityFollowDepth(cfg.intel_max_entity_follow_depth);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Config yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSave() {
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      // Derive `tts_disabled` from the engine selector so the persisted
      // config is internally consistent — backend already does the same
      // normalisation but sending the derived flag avoids a round-trip
      // race on hot-reload.
      const isOff = ttsEngine === "off";
      const payload: SystemConfig = {
        llm_default_model: llmModel,
        llm_temperature: temperature,
        llm_top_p: topP,
        llm_max_tokens: maxTokens,
        llm_presence_penalty: presencePenalty,
        llm_frequency_penalty: frequencyPenalty,
        // Empty string sentinel ⇒ NULL on the wire ⇒ backend falls
        // back to the catalogue default. exactOptionalPropertyTypes
        // requires us to OMIT the key when no override is set.
        ...(commentaryModelId
          ? { commentary_model_id: commentaryModelId }
          : { commentary_model_id: null }),
        ...(comparisonModelId
          ? { comparison_model_id: comparisonModelId }
          : { comparison_model_id: null }),
        tts_engine: ttsEngine as "edge-tts" | "off",
        tts_disabled: isOff,
        tts_speed: ttsSpeed,
        // Only send the Brave key if the admin actually typed a new
        // value — never resend the loaded masked "••••1234" display.
        ...(braveApiKeyDirty ? { research_brave_api_key: braveApiKey } : {}),
        // İstihbarat Ajanı Faz 0 — same dirty-guard contract for Google
        // CSE / Yandex.
        ...(googleCseKeyDirty ? { research_google_cse_key: googleCseKey } : {}),
        ...(googleCseCxDirty ? { research_google_cse_cx: googleCseCx } : {}),
        ...(yandexApiKeyDirty ? { research_yandex_api_key: yandexApiKey } : {}),
        ...(yandexUserDirty ? { research_yandex_user: yandexUser } : {}),
        // İstihbarat Ajanı Faz 5 — capability flags + budgets, always sent
        // (no secret-masking concern for booleans/ints).
        intel_vision_enabled: intelVisionEnabled,
        intel_entity_follow_enabled: intelEntityFollowEnabled,
        intel_dossier_enabled: intelDossierEnabled,
        intel_screenshot_enabled: intelScreenshotEnabled,
        intel_pdf_reader_enabled: intelPdfReaderEnabled,
        intel_max_images_per_turn: intelMaxImagesPerTurn,
        intel_max_entity_follow_depth: intelMaxEntityFollowDepth,
        default_max_rounds: defaultMaxRounds,
        hard_cap_rounds: hardCapRounds,
        ...(cheapModel ? { cheap_model: cheapModel } : {}),
      };
      await updateSystemConfig(payload);
      setSuccess(true);
      setBraveApiKeyDirty(false);
      setGoogleCseKeyDirty(false);
      setGoogleCseCxDirty(false);
      setYandexApiKeyDirty(false);
      setYandexUserDirty(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Sistem ayarları yükleniyor…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Sistem Ayarları</h2>
          <p className="text-xs text-slate-500 mt-1">
            Bu ayarlar tüm kullanıcıları etkiler. Değişiklikler anında uygulanır — restart gerektirmez.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {success && <span className="text-sm text-emerald-400">✓ Kaydedildi</span>}
          {error && <span className="text-sm text-red-400">{error}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-medium rounded-xl shadow-lg shadow-indigo-500/20 disabled:opacity-40 transition-all"
          >
            {saving ? "Kaydediliyor…" : "Kaydet & Uygula"}
          </button>
        </div>
      </div>

      {/* LLM Settings */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400 text-sm font-bold">
            AI
          </div>
          <div>
            <h3 className="font-semibold">LLM Çıkarım Ayarları</h3>
            <p className="text-xs text-slate-500">Tartışmada kullanılan modelin parametreleri</p>
          </div>
        </div>

        {/* Model Grid */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-3">Varsayılan Model</label>
          <div className="grid grid-cols-2 gap-3">
            {modelOptions.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setLlmModel(m.id)}
                className={`text-left px-4 py-3 rounded-xl border transition-all ${
                  llmModel === m.id
                    ? "border-indigo-500/60 bg-indigo-500/10 ring-1 ring-indigo-500/30"
                    : "border-white/8 bg-white/[0.02] hover:border-white/15"
                }`}
              >
                <p className={`text-sm font-medium ${llmModel === m.id ? "text-indigo-300" : "text-slate-300"}`}>
                  {m.label}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{m.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Sampling */}
        <div className="grid grid-cols-2 gap-6">
          <SliderField value={temperature} onChange={setTemperature} min={0} max={1.5} step={0.05} label="Sıcaklık (Yaratıcılık)" desc="0 = deterministik · 1.5 = çok yaratıcı" />
          <SliderField value={topP} onChange={setTopP} min={0.5} max={1.0} step={0.01} label="Üst-P (Nucleus)" desc="Olasılık dağılım penceresi genişliği" />
          <SliderField value={maxTokens} onChange={(v) => setMaxTokens(Math.round(v))} min={256} max={4096} step={128} label="Maks. Token" desc="Konuşmacı başına azami çıktı uzunluğu" />
          <SliderField value={presencePenalty} onChange={setPresencePenalty} min={-1} max={1} step={0.1} label="Yenilik Cezası" desc="Yeni konulara yönlendirme şiddeti" />
          <SliderField value={frequencyPenalty} onChange={setFrequencyPenalty} min={-1} max={1} step={0.1} label="Tekrar Cezası" desc="Tekrarlanan ifadeleri caydırma" />
        </div>
      </section>

      {/*
        SUBSYSTEM MODEL OVERRIDES — separate model knobs for the
        editorial commentary engine and the AI comparison reporter.
        Both default to the catalogue default; admins typically pin
        a higher-context model here (e.g. brain-31b deep) without
        disturbing the lower-latency default used by debate runtime.
      */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 text-sm font-bold">
            ✎
          </div>
          <div>
            <h3 className="font-semibold">Alt Sistem Model Atamaları</h3>
            <p className="text-xs text-slate-500">
              Editoryal yorum ve AI kıyaslama için bağımsız model seçimi.
              Boş bırakılırsa varsayılan model kullanılır.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ModelPicker
            label="Editoryal Yorum Modeli"
            desc="Tartışma sonunda otomatik üretilen yorumun ve manuel ek yorumların kaynağı"
            value={commentaryModelId}
            onChange={setCommentaryModelId}
            options={modelOptions}
            accent="amber"
          />
          <ModelPicker
            label="AI Kıyaslama Modeli"
            desc="İki tartışma versiyonunu karşılaştıran rapor üreticisi"
            value={comparisonModelId}
            onChange={setComparisonModelId}
            options={modelOptions}
            accent="amber"
          />
        </div>
      </section>

      {/* TTS Settings */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 text-sm font-bold">
            TTS
          </div>
          <div>
            <h3 className="font-semibold">Ses Sentezi (TTS) Ayarları</h3>
            <p className="text-xs text-slate-500">Konuşmacı ses üretim motoru ve parametreleri</p>
          </div>
        </div>

        {/* Engine Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-3">TTS Motoru</label>
          <div className="grid grid-cols-2 gap-3">
            {TTS_ENGINE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  setTtsEngine(opt.id);
                }}
                className={`text-left px-4 py-3 rounded-xl border transition-all ${
                  ttsEngine === opt.id
                    ? "border-cyan-500/60 bg-cyan-500/10 ring-1 ring-cyan-500/30"
                    : "border-white/8 bg-white/[0.02] hover:border-white/15"
                }`}
              >
                <p className={`text-sm font-medium ${ttsEngine === opt.id ? "text-cyan-300" : "text-slate-300"}`}>
                  {opt.label}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Speed */}
        {ttsEngine !== "off" && (
          <SliderField value={ttsSpeed} onChange={setTtsSpeed} min={0.5} max={2.0} step={0.1} label="Konuşma Hızı" desc="1.0 = normal, 0.7 = yavaş, 1.5 = hızlı" suffix="x" />
        )}
      </section>

      {/* Tartışma bütçeleri — parameter_catalog'tan taşındı (2026-07-25) */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-400 text-sm font-bold">
            TUR
          </div>
          <div>
            <h3 className="font-semibold">Tartışma Bütçeleri</h3>
            <p className="text-xs text-slate-500">
              Tur sayısı sınırları ve ucuz-yol model seçimi. Kaydettiğinizde bir
              sonraki tartışmada etkili — servis yeniden başlatmaya gerek yok.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Varsayılan Tur Sayısı
            </label>
            <p className="text-xs text-slate-500 mb-2">
              İstek belirtmezse bir tartışmanın kaç tur süreceği.
            </p>
            <input
              type="number"
              min={1}
              max={64}
              value={defaultMaxRounds}
              onChange={(e) => setDefaultMaxRounds(parseInt(e.target.value) || 5)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Mutlak Tur Sınırı
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Güvenlik tavanı — hiçbir istek bunu aşamaz. Varsayılandan küçük olamaz.
            </p>
            <input
              type="number"
              min={1}
              max={64}
              value={hardCapRounds}
              onChange={(e) => setHardCapRounds(parseInt(e.target.value) || 50)}
              className={`w-full px-3 py-2 bg-white/5 border rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-violet-500 ${
                hardCapRounds < defaultMaxRounds ? "border-red-500/60" : "border-white/10"
              }`}
            />
            {hardCapRounds < defaultMaxRounds && (
              <p className="text-xs text-red-400 mt-1">
                Mutlak sınır, varsayılan tur sayısından küçük olamaz.
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <ModelPicker
            label="Ucuz Yol Modeli"
            desc="Değerlendirme/puanlama gibi düşük maliyetli işler için kullanılacak model"
            value={cheapModel}
            onChange={setCheapModel}
            options={modelOptions}
            accent="indigo"
          />
        </div>
      </section>

      {/* İş Kolu D1 — Web Research backend keys */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-400 text-sm font-bold">
            WEB
          </div>
          <div>
            <h3 className="font-semibold">Web Araştırma Anahtarları</h3>
            <p className="text-xs text-slate-500">
              SSH/restart gerektirmeden — kaydettiğinizde bir sonraki debate'te anında etkili.
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Brave Search API Key</label>
          <input
            type="password"
            value={braveApiKey}
            onChange={(e) => {
              setBraveApiKey(e.target.value);
              setBraveApiKeyDirty(true);
            }}
            placeholder="BSA... (boş bırakılırsa Brave backend devre dışı kalır)"
            className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-slate-200 outline-none focus:border-orange-500/60"
          />
          <p className="mt-2 text-xs text-slate-500">
            "Web Araştırma Kaynakları" sekmesindeki "brave" satırını "Test Et" ile
            doğrulayın. Boş/geçersiz anahtar Brave'i sessizce devre dışı bırakır —
            diğer 17 backend (Wikipedia, Searxng, arXiv, PubMed, vb.) bundan
            etkilenmez.
          </p>
        </div>

        {/* İstihbarat Ajanı Faz 0 — Google Programmable Search + Yandex XML API,
            same hot-reload contract as Brave above. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-white/5">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Google Programmable Search — API Key</label>
            <input
              type="password"
              value={googleCseKey}
              onChange={(e) => { setGoogleCseKey(e.target.value); setGoogleCseKeyDirty(true); }}
              placeholder="AIza... (boş bırakılırsa google-cse backend anahtarsız kalır)"
              className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-slate-200 outline-none focus:border-orange-500/60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Google Programmable Search — Engine ID (cx)</label>
            <input
              type="password"
              value={googleCseCx}
              onChange={(e) => { setGoogleCseCx(e.target.value); setGoogleCseCxDirty(true); }}
              placeholder="Arama motoru kimliği (cx)"
              className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-slate-200 outline-none focus:border-orange-500/60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Yandex XML Search — API Key</label>
            <input
              type="password"
              value={yandexApiKey}
              onChange={(e) => { setYandexApiKey(e.target.value); setYandexApiKeyDirty(true); }}
              placeholder="Yandex API anahtarı"
              className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-slate-200 outline-none focus:border-orange-500/60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Yandex XML Search — User</label>
            <input
              type="password"
              value={yandexUser}
              onChange={(e) => { setYandexUser(e.target.value); setYandexUserDirty(true); }}
              placeholder="Yandex kullanıcı adı"
              className="w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-slate-200 outline-none focus:border-orange-500/60"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Google CSE her iki alan (KEY+CX), Yandex her iki alan (KEY+USER) dolu olmadan
          etkinleşmez — kısmi doldurma backend'i anahtarsız (no-op) bırakır.
        </p>
      </section>

      {/* İstihbarat Ajanı Faz 5 — capability feature-flags + budgets */}
      <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-400 text-sm font-bold">
            İA
          </div>
          <div>
            <h3 className="font-semibold">İstihbarat Ajanı — Yetenek Bayrakları</h3>
            <p className="text-xs text-slate-500">
              Her yetenek ayrı açılıp kapanabilir; kapalı yetenek sistemi bozmadan devre dışı kalır (graceful degrade).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ToggleField
            label="Vision (görsel okuma)"
            desc="Kaynaklardaki görsel/infografikleri vision-LLM ile okur."
            checked={intelVisionEnabled}
            onChange={setIntelVisionEnabled}
          />
          <ToggleField
            label="Entity-follow (varlık takibi)"
            desc="Çıkarılan varlıklar (kişi/kurum) için ek takip araması yapar."
            checked={intelEntityFollowEnabled}
            onChange={setIntelEntityFollowEnabled}
          />
          <ToggleField
            label="Dossier (istihbarat dosyası)"
            desc="Timeline/entities/claims üretir; digest tavanına sıkışmaz."
            checked={intelDossierEnabled}
            onChange={setIntelDossierEnabled}
          />
          <ToggleField
            label="Screenshot (JS-yoğun sayfa)"
            desc="stealth_fetch ile ekran görüntüsü alıp vision ile okur."
            checked={intelScreenshotEnabled}
            onChange={setIntelScreenshotEnabled}
          />
          <ToggleField
            label="PDF okuyucu"
            desc="PDF kaynaklarını metin+grafik olarak işler (önceden SKIP idi)."
            checked={intelPdfReaderEnabled}
            onChange={setIntelPdfReaderEnabled}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-white/5">
          <SliderField
            label="Tur başına maksimum görsel"
            desc="Vision maliyeti/latency bütçesi — turda en fazla bu kadar görsel okunur."
            value={intelMaxImagesPerTurn}
            onChange={setIntelMaxImagesPerTurn}
            min={0}
            max={10}
            step={1}
          />
          <SliderField
            label="Maksimum varlık-takip derinliği"
            desc="Fanout patlamasını önlemek için takip aramasının derinlik sınırı."
            value={intelMaxEntityFollowDepth}
            onChange={setIntelMaxEntityFollowDepth}
            min={0}
            max={3}
            step={1}
          />
        </div>
      </section>

      {/* Bottom save */}
      <div className="flex items-center gap-3 pb-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-medium rounded-xl shadow-lg shadow-indigo-500/20 disabled:opacity-40 transition-all text-sm"
        >
          {saving ? "Kaydediliyor…" : "Tüm Sistem Ayarlarını Kaydet"}
        </button>
        {success && <span className="text-sm text-emerald-400">✓ Kaydedildi — anında etkin</span>}
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function SliderField({ value, onChange, min, max, step, label, desc, suffix = "" }: {
  value: number; onChange: (v: number) => void; min: number; max: number; step: number;
  label: string; desc: string; suffix?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-slate-300">{label}</label>
        <span className="font-mono text-xs text-indigo-400 tabular-nums">
          {value.toFixed(step < 1 ? 2 : 0)}{suffix}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">{desc}</p>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-indigo-500"
      />
      <div className="flex justify-between text-[10px] text-slate-600 mt-0.5">
        <span>{min}{suffix}</span>
        <span>{max}{suffix}</span>
      </div>
    </div>
  );
}

function ToggleSwitch({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-cyan-600" : "bg-slate-700"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

/**
 * ToggleField — İstihbarat Ajanı Faz 0/5 — label + description + switch,
 * used for per-capability feature-flags (vision, entity-follow, dossier,
 * screenshot, pdf-reader) in the System Config "Yetenek Bayrakları" panel.
 */
function ToggleField({ label, desc, checked, onChange }: {
  label: string; desc: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-white/5 bg-slate-950/40 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-200">{label}</div>
        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
      </div>
      <ToggleSwitch enabled={checked} onChange={onChange} />
    </div>
  );
}

/**
 * ModelPicker — segmented model selector with an explicit "Varsayılan"
 * sentinel (empty string ⇒ NULL on the wire ⇒ catalogue default).
 *
 * Used by the Subsystem Model Overrides admin section so an empty
 * selection is a first-class state, not a UI-induced footgun: an admin
 * can deliberately revert a pinned model back to "default" with a
 * single click, and the persisted system_config row stores NULL so
 * future seed-model rotations apply automatically.
 */
function ModelPicker({
  label,
  desc,
  value,
  onChange,
  options,
  accent = "indigo",
}: {
  label: string;
  desc: string;
  value: string;
  onChange: (v: string) => void;
  options: LlmModel[];
  accent?: "indigo" | "cyan" | "amber";
}) {
  const accentMap: Record<string, { ring: string; text: string; chip: string }> = {
    indigo: {
      ring: "border-indigo-500/60 bg-indigo-500/10 ring-1 ring-indigo-500/30",
      text: "text-indigo-300",
      chip: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
    },
    cyan: {
      ring: "border-cyan-500/60 bg-cyan-500/10 ring-1 ring-cyan-500/30",
      text: "text-cyan-300",
      chip: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
    },
    amber: {
      ring: "border-amber-500/60 bg-amber-500/10 ring-1 ring-amber-500/30",
      text: "text-amber-300",
      chip: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    },
  };
  const a = accentMap[accent] ?? accentMap.indigo!;
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
      <p className="text-[11px] text-slate-500 mb-3">{desc}</p>
      <div className="grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => onChange("")}
          className={`text-left px-4 py-3 rounded-xl border transition-all ${
            value === "" ? a.ring : "border-white/8 bg-white/[0.02] hover:border-white/15"
          }`}
        >
          <p className={`text-sm font-medium ${value === "" ? a.text : "text-slate-300"}`}>
            Varsayılan Model (otomatik)
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            `models_catalog` içinde `is_default=true` işaretli model kullanılır
          </p>
        </button>
        {options.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            className={`text-left px-4 py-3 rounded-xl border transition-all ${
              value === m.id ? a.ring : "border-white/8 bg-white/[0.02] hover:border-white/15"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${value === m.id ? a.text : "text-slate-300"}`}>
                  {m.label}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5 truncate">{m.description}</p>
              </div>
              <span
                className={`shrink-0 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${a.chip}`}
              >
                {m.speed_tier}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODELS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const EMPTY_MODEL: LlmModel = {
  id: "",
  label: "",
  family: "dai-core",
  backend_model: "",
  base_url: "",
  api_key: "not-required-for-vllm",
  context_len: 32768,
  speed_tier: "balanced",
  description: "",
  supports_tool_call: false,
  supports_streaming: true,
  supports_vision: false,
  supports_reasoning: false,
  tool_call_parser: null,
  reasoning_parser: null,
  default_temperature: 0.7,
  default_top_p: 0.92,
  default_max_tokens: 1500,
  default_presence_penalty: 0.0,
  default_frequency_penalty: 0.0,
  fits: [],
  sort_order: 0,
  is_active: true,
  is_default: false,
  is_fast_route: false,
  is_speaking_model: false,
};

function ModelsTab() {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<LlmModel | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await adminListModels();
      setModels(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id: string) {
    if (!confirm(`"${id}" modelini silmek istediğinize emin misiniz?`)) return;
    try {
      await adminDeleteModel(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Silme başarısız");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">LLM Model Yönetimi</h2>
          <p className="text-xs text-slate-500 mt-1">Model ekleyin, düzenleyin veya kaldırın. Değişiklikler anında uygulanır.</p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...EMPTY_MODEL }); }}
          className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium transition-colors"
        >
          + Model Ekle
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {editing && (
        <ModelEditForm
          model={editing}
          isNew={creating}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
          onCancel={() => { setEditing(null); setCreating(false); }}
        />
      )}

      <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Model</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Backend</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Yetenekler</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Durum</th>
              <th className="text-right px-4 py-3 text-xs text-slate-500 uppercase font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Yükleniyor…</td></tr>
            ) : models.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Model bulunamadı</td></tr>
            ) : (
              models.map((m) => (
                <tr key={m.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium">{m.label}</p>
                    <p className="text-xs text-slate-500 font-mono">{m.id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-mono text-slate-400">{m.backend_model}</p>
                    <p className="text-[10px] text-slate-600 truncate max-w-[200px]">{m.base_url}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {m.supports_streaming && <Badge color="green">Akış</Badge>}
                      {m.supports_tool_call && <Badge color="blue">Araç Çağrısı</Badge>}
                      {m.supports_vision && <Badge color="purple">Görme</Badge>}
                      {m.supports_reasoning && <Badge color="amber">Muhakeme</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className={`inline-flex items-center gap-1 text-xs ${m.is_active ? "text-emerald-400" : "text-red-400"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${m.is_active ? "bg-emerald-400" : "bg-red-400"}`} />
                        {m.is_active ? "Aktif" : "Pasif"}
                      </span>
                      {m.is_default && <span className="text-[10px] text-indigo-400">★ Varsayılan</span>}
                      {m.is_fast_route && <span className="text-[10px] text-cyan-400">⚡ Hızlı Rota</span>}
                      {m.is_speaking_model && <span className="text-[10px] text-emerald-400">🗣 Konuşan Model</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setEditing(m); setCreating(false); }}
                      className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors mr-1"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="text-xs text-red-400/70 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  const colors: Record<string, string> = {
    green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
}

function ModelEditForm({ model, isNew, onSaved, onCancel }: {
  model: LlmModel; isNew: boolean; onSaved: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState<LlmModel>(model);
  const [fitsText, setFitsText] = useState(model.fits.join(", "));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ModelTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof LlmModel>(key: K, val: LlmModel[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = { ...form, fits: fitsText.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (isNew) {
        await adminCreateModel(payload);
      } else {
        await adminUpdateModel(model.id, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection() {
    if (isNew) {
      setError("Önce modeli kaydedin, sonra test edin.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await adminTestModel(model.id);
      setTestResult(result);
    } catch (err) {
      setTestResult({
        ok: false,
        latency_ms: 0,
        served_models: [],
        error: err instanceof Error ? err.message : "Test başarısız",
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
      <h3 className="font-semibold mb-4">{isNew ? "Yeni Model" : `Düzenle: ${model.label}`}</h3>
      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Row 1: ID + Label */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Model Kimliği (slug)</label>
            <input
              type="text"
              required
              disabled={!isNew}
              pattern="^[a-z0-9][a-z0-9\-]*$"
              value={form.id}
              onChange={(e) => set("id", e.target.value)}
              placeholder="ornek-model"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Görüntü Adı</label>
            <input
              type="text"
              required
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
              placeholder="DAI Konsey-XYZ"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Row 2: Backend + URL */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Arka Uç Modeli (vLLM served-model-name)</label>
            <input
              type="text"
              required
              value={form.backend_model}
              onChange={(e) => set("backend_model", e.target.value)}
              placeholder="crudfab-brain"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Base URL</label>
            <input
              type="url"
              required
              value={form.base_url}
              onChange={(e) => set("base_url", e.target.value)}
              placeholder="http://127.0.0.1:8000/v1"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Row 3: Family + Context + Speed */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Aile</label>
            <select
              value={form.family}
              onChange={(e) => set("family", e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {MODEL_FAMILY_OPTIONS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Bağlam Uzunluğu</label>
            <input
              type="number"
              value={form.context_len}
              onChange={(e) => set("context_len", parseInt(e.target.value) || 32768)}
              min={1024}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Hız Seviyesi</label>
            <select
              value={form.speed_tier}
              onChange={(e) => set("speed_tier", e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="fast">Hızlı</option>
              <option value="balanced">Dengeli</option>
              <option value="deep">Derin</option>
            </select>
          </div>
        </div>

        {/* Row 3.5: API Key (was hidden — now exposed for non-vLLM providers) */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">
            API Anahtarı <span className="text-slate-600">(vLLM için gerekmez — boş bırakın)</span>
          </label>
          <input
            type="text"
            value={form.api_key}
            onChange={(e) => set("api_key", e.target.value)}
            placeholder="not-required-for-vllm"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {/* Row 4: Description */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Açıklama</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Modelin kısa açıklaması"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {/* Row 5: Capabilities */}
        <div>
          <label className="block text-xs text-slate-500 mb-2">Yetenekler</label>
          <div className="flex flex-wrap gap-4">
            <CapToggle label="Akış" checked={form.supports_streaming} onChange={(v) => set("supports_streaming", v)} />
            <CapToggle label="Araç Çağrısı" checked={form.supports_tool_call} onChange={(v) => set("supports_tool_call", v)} />
            <CapToggle label="Görme" checked={form.supports_vision} onChange={(v) => set("supports_vision", v)} />
            <CapToggle label="Muhakeme" checked={form.supports_reasoning} onChange={(v) => set("supports_reasoning", v)} />
          </div>
        </div>

        {/* Row 6: Parsers */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Araç Çağrısı Çözümleyici</label>
            <select
              value={form.tool_call_parser || ""}
              onChange={(e) => set("tool_call_parser", e.target.value || null)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Yok</option>
              <option value="gemma4">gemma4</option>
              <option value="hermes">hermes</option>
              <option value="llama4">llama4</option>
              <option value="mistral">mistral</option>
              <option value="internlm">internlm</option>
              <option value="jamba">jamba</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Muhakeme Çözümleyici</label>
            <select
              value={form.reasoning_parser || ""}
              onChange={(e) => set("reasoning_parser", e.target.value || null)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Yok</option>
              <option value="gemma4">gemma4</option>
              <option value="deepseek_r1">deepseek_r1</option>
              <option value="qwen3">qwen3</option>
            </select>
          </div>
        </div>

        {/* Row 7: Sampling Defaults */}
        <div className="grid grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sıcaklık</label>
            <input type="number" step={0.05} min={0} max={2} value={form.default_temperature}
              onChange={(e) => set("default_temperature", parseFloat(e.target.value) || 0.7)}
              className="w-full px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Üst-P</label>
            <input type="number" step={0.01} min={0} max={1} value={form.default_top_p}
              onChange={(e) => set("default_top_p", parseFloat(e.target.value) || 0.92)}
              className="w-full px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Maks. Token</label>
            <input type="number" step={64} min={64} max={65536} value={form.default_max_tokens}
              onChange={(e) => set("default_max_tokens", parseInt(e.target.value) || 1500)}
              className="w-full px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Yenilik C.</label>
            <input type="number" step={0.1} min={-2} max={2} value={form.default_presence_penalty}
              onChange={(e) => set("default_presence_penalty", parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Tekrar C.</label>
            <input type="number" step={0.1} min={-2} max={2} value={form.default_frequency_penalty}
              onChange={(e) => set("default_frequency_penalty", parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
        </div>

        {/* Row 8: Fits */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Uygun Roller (virgülle ayır)</label>
          <input
            type="text"
            value={fitsText}
            onChange={(e) => setFitsText(e.target.value)}
            placeholder="reasoning, code, general, creative"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {/* Row 9: Flags */}
        <div className="flex flex-wrap gap-6">
          <CapToggle label="Aktif" checked={form.is_active} onChange={(v) => set("is_active", v)} />
          <CapToggle label="Varsayılan Model" checked={form.is_default} onChange={(v) => set("is_default", v)} />
          <CapToggle label="Hızlı Rota" checked={form.is_fast_route} onChange={(v) => set("is_fast_route", v)} />
          <CapToggle
            label="Konuşan Model (transkript-görünür)"
            checked={form.is_speaking_model}
            onChange={(v) => set("is_speaking_model", v)}
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Sıralama:</label>
            <input type="number" min={0} max={999} value={form.sort_order}
              onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)}
              className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
        </div>

        {/* Test result */}
        {testResult && (
          <div
            className={`px-4 py-3 rounded-lg text-sm border ${
              testResult.ok
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="font-medium">
                {testResult.ok ? "✓ Bağlantı başarılı" : "✗ Bağlantı başarısız"}
              </div>
              <div className="text-xs opacity-70 font-mono">
                {testResult.latency_ms}ms
              </div>
            </div>
            {testResult.error && (
              <div className="text-xs mt-1 opacity-80 font-mono">{testResult.error}</div>
            )}
            {testResult.ok && testResult.served_models.length > 0 && (
              <div className="text-xs mt-1.5 opacity-80">
                Sunulan modeller:{" "}
                <span className="font-mono">{testResult.served_models.slice(0, 5).join(", ")}</span>
                {testResult.matches === false && (
                  <span className="ml-2 text-amber-400">
                    ⚠ "{form.backend_model}" listede yok
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium disabled:opacity-40 transition-colors"
          >
            {saving ? "Kaydediliyor…" : isNew ? "Oluştur" : "Güncelle"}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing}
              className="px-4 py-2 text-sm text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/60 hover:bg-cyan-500/10 rounded-lg font-medium disabled:opacity-40 transition-colors"
            >
              {testing ? "Test ediliyor…" : "Bağlantıyı Test Et"}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}

function CapToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/30"
      />
      <span className="text-sm text-slate-300">{label}</span>
    </label>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MCP SERVERS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const EMPTY_MCP_SERVER: McpServerPayload = {
  name: "",
  description: "",
  transport: "mcp_http",
  url: "",
  auth_header: null,
  default_tool: null,
  timeout_s: 10,
  enable_outbound: true,
  enable_tools: false,
  is_active: true,
  sort_order: 0,
};

function McpServersTab() {
  const [servers, setServers] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ payload: McpServerPayload; id: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setServers(await adminListMcpServers());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function handleDelete(s: McpServer) {
    if (!confirm(`"${s.name}" MCP sunucusunu silmek istediğinize emin misiniz?`)) return;
    try {
      await adminDeleteMcpServer(s.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Silme başarısız");
    }
  }

  async function handleTest(s: McpServer) {
    setTestingId(s.id);
    try {
      await adminTestMcpServer(s.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Test başarısız");
    } finally {
      setTestingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">MCP Sunucuları</h2>
          <p className="text-xs text-slate-500 mt-1">
            Giden iletim hedefleri (karar gönderimi) ve gelen MCP araçları (tartışmada
            araç çağrıları). Model Context Protocol veya düz webhook üzerinden iletişim kurar.
          </p>
        </div>
        <button
          onClick={() => setEditing({ payload: { ...EMPTY_MCP_SERVER }, id: null })}
          className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium transition-colors"
        >
          + MCP Sunucusu Ekle
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {editing && (
        <McpServerEditForm
          payload={editing.payload}
          serverId={editing.id}
          onSaved={() => { setEditing(null); void load(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Sunucu</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Taşıma</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Yetenekler</th>
              <th className="text-left px-4 py-3 text-xs text-slate-500 uppercase font-medium">Sağlık</th>
              <th className="text-right px-4 py-3 text-xs text-slate-500 uppercase font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Yükleniyor…</td></tr>
            ) : servers.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                Henüz MCP sunucusu eklenmemiş. Tartışma sonuçlarını harici sistemlere
                göndermek için bir hedef ekleyin (Slack, Linear, Jira, özel webhook…).
              </td></tr>
            ) : (
              servers.map((s) => (
                <tr key={s.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-slate-500 font-mono truncate max-w-[260px]">{s.url}</p>
                    {s.description && <p className="text-[10px] text-slate-600 mt-0.5 max-w-[260px] truncate">{s.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={s.transport === "mcp_http" ? "purple" : "blue"}>
                      {s.transport === "mcp_http" ? "MCP HTTP" : "Webhook"}
                    </Badge>
                    {s.default_tool && (
                      <p className="text-[10px] text-slate-500 mt-1 font-mono">araç: {s.default_tool}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {s.enable_outbound && <Badge color="green">Giden</Badge>}
                      {s.enable_tools && <Badge color="amber">Araçlar</Badge>}
                      {!s.is_active && <Badge color="red">Pasif</Badge>}
                      {s.discovered_tools.length > 0 && (
                        <span className="text-[10px] text-slate-500">
                          {s.discovered_tools.length} araç keşfedildi
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <McpHealthBadge server={s} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleTest(s)}
                      disabled={testingId === s.id}
                      className="text-xs text-cyan-400/80 hover:text-cyan-300 px-2 py-1 rounded hover:bg-cyan-500/10 transition-colors mr-1 disabled:opacity-40"
                    >
                      {testingId === s.id ? "Test…" : "Test"}
                    </button>
                    <button
                      onClick={() => setEditing({
                        payload: {
                          name: s.name,
                          description: s.description,
                          transport: s.transport,
                          url: s.url,
                          auth_header: s.auth_header,
                          default_tool: s.default_tool,
                          timeout_s: s.timeout_s,
                          enable_outbound: s.enable_outbound,
                          enable_tools: s.enable_tools,
                          is_active: s.is_active,
                          sort_order: s.sort_order,
                        },
                        id: s.id,
                      })}
                      className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors mr-1"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => handleDelete(s)}
                      className="text-xs text-red-400/70 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function McpHealthBadge({ server }: { server: McpServer }) {
  const cfg = server.last_health === "ok"
    ? { color: "bg-emerald-400", label: "Aktif", textColor: "text-emerald-400" }
    : server.last_health === "error"
    ? { color: "bg-red-400", label: "Hata", textColor: "text-red-400" }
    : { color: "bg-slate-500", label: "Bilinmiyor", textColor: "text-slate-500" };
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1.5 text-xs ${cfg.textColor}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
        {cfg.label}
      </span>
      {server.last_error && (
        <span className="text-[10px] text-slate-500 truncate max-w-[160px]" title={server.last_error}>
          {server.last_error}
        </span>
      )}
      {server.last_checked_at && (
        <span className="text-[10px] text-slate-600">
          {new Date(server.last_checked_at).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
        </span>
      )}
    </div>
  );
}

function McpServerEditForm({
  payload,
  serverId,
  onSaved,
  onCancel,
}: {
  payload: McpServerPayload;
  serverId: string | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<McpServerPayload>(payload);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<McpTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isNew = serverId === null;

  function set<K extends keyof McpServerPayload>(key: K, val: McpServerPayload[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await adminCreateMcpServer(form);
      } else {
        await adminUpdateMcpServer(serverId!, form);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (isNew) {
      setError("Önce sunucuyu kaydedin, sonra test edin.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminTestMcpServer(serverId!);
      setTestResult(res);
    } catch (err) {
      setTestResult({
        ok: false,
        tools: [],
        error: err instanceof Error ? err.message : "Test başarısız",
        latency_ms: 0,
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
      <h3 className="font-semibold mb-4">{isNew ? "Yeni MCP Sunucusu" : `Düzenle: ${form.name}`}</h3>
      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Row 1: Name + Transport */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sunucu Adı (kod tarafı)</label>
            <input
              type="text"
              required
              pattern="^[a-zA-Z0-9][a-zA-Z0-9_\-]*$"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="slack-team-eng"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Taşıma Katmanı</label>
            <select
              value={form.transport}
              onChange={(e) => set("transport", e.target.value as "mcp_http" | "webhook")}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="mcp_http">MCP HTTP (JSON-RPC 2.0)</option>
              <option value="webhook">Webhook (Düz JSON POST)</option>
            </select>
          </div>
        </div>

        {/* Row 2: URL */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">URL</label>
          <input
            type="url"
            required
            value={form.url}
            onChange={(e) => set("url", e.target.value)}
            placeholder="https://mcp.example.com/jsonrpc"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {/* Row 3: Auth Header + Default Tool + Timeout */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Yetkilendirme Başlığı <span className="text-slate-600">(opsiyonel)</span>
            </label>
            <input
              type="text"
              value={form.auth_header ?? ""}
              onChange={(e) => set("auth_header", e.target.value || null)}
              placeholder="Bearer xxx-yyy-zzz"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Varsayılan Araç</label>
            <input
              type="text"
              value={form.default_tool ?? ""}
              onChange={(e) => set("default_tool", e.target.value || null)}
              placeholder="createIssue"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Zaman Aşımı (saniye)</label>
            <input
              type="number"
              min={1}
              max={60}
              step={1}
              value={form.timeout_s}
              onChange={(e) => set("timeout_s", parseFloat(e.target.value) || 10)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Row 4: Description */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Açıklama</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Mühendislik takımı Slack kanalı"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {/* Row 5: Capabilities */}
        <div>
          <label className="block text-xs text-slate-500 mb-2">Yetenekler</label>
          <div className="flex flex-wrap gap-6">
            <CapToggle
              label="Giden (Karar İletimi)"
              checked={form.enable_outbound}
              onChange={(v) => set("enable_outbound", v)}
            />
            <CapToggle
              label="Araçlar (Tartışmada Araç Çağrısı)"
              checked={form.enable_tools}
              onChange={(v) => set("enable_tools", v)}
            />
            <CapToggle
              label="Aktif"
              checked={form.is_active}
              onChange={(v) => set("is_active", v)}
            />
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Sıralama:</label>
              <input
                type="number"
                min={0}
                max={999}
                value={form.sort_order}
                onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>


        {/* Test result */}
        {testResult && (
          <div
            className={`px-4 py-3 rounded-lg text-sm border ${
              testResult.ok
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="font-medium">
                {testResult.ok ? "✓ Bağlantı başarılı" : "✗ Bağlantı başarısız"}
              </div>
              <div className="text-xs opacity-70 font-mono">{testResult.latency_ms}ms</div>
            </div>
            {testResult.error && (
              <div className="text-xs mt-1 opacity-80 font-mono">{testResult.error}</div>
            )}
            {testResult.ok && testResult.tools.length > 0 && (
              <div className="text-xs mt-2 opacity-90">
                <div className="font-medium mb-1">Keşfedilen tool'lar ({testResult.tools.length}):</div>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                  {testResult.tools.slice(0, 8).map((t, i) => (
                    <li key={i} className="font-mono text-[11px]">
                      <span className="text-emerald-300">{String(t.name ?? "?")}</span>
                      {t.description ? (
                        <span className="text-slate-400"> — {String(t.description).slice(0, 80)}</span>
                      ) : null}
                    </li>
                  ))}
                  {testResult.tools.length > 8 && (
                    <li className="text-[11px] text-slate-500">… ve {testResult.tools.length - 8} daha</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium disabled:opacity-40 transition-colors"
          >
            {saving ? "Kaydediliyor…" : isNew ? "Oluştur" : "Güncelle"}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="px-4 py-2 text-sm text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/60 hover:bg-cyan-500/10 rounded-lg font-medium disabled:opacity-40 transition-colors"
            >
              {testing ? "Test ediliyor…" : "Bağlantıyı Test Et"}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}


function CreateUserForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<CreateUserPayload>({
    email: "",
    password: "",
    display_name: "",
    role: "user",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await adminCreateUser(form);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Oluşturma hatası");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-6 bg-white/[0.03] border border-white/10 rounded-2xl p-6">
      <h3 className="font-semibold mb-4">Yeni Kullanıcı</h3>
      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
      <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
        <input
          type="text"
          placeholder="İsim"
          required
          value={form.display_name}
          onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <input
          type="email"
          placeholder="E-posta"
          required
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <input
          type="password"
          placeholder="Şifre (min 6 karakter)"
          required
          minLength={6}
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <select
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="user">Kullanıcı</option>
          <option value="admin">Admin</option>
        </select>
        <div className="col-span-2 flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium disabled:opacity-40 transition-colors"
          >
            {saving ? "Oluşturuluyor…" : "Oluştur"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// RESEARCH BACKENDS TAB — admin-managed web search source pool
// ═══════════════════════════════════════════════════════════════════════════════

// İstihbarat Ajanı Faz 0 — admin-panel category grouping. Mirrors the
// server-side `_backend_category` map in `routes_admin.py`; used ONLY
// for filter-tab labels/icons here (the authoritative grouping is the
// `category` field the backend already returns per row).
const RESEARCH_CATEGORY_LABELS: Record<string, string> = {
  all: "Tümü",
  web: "🌐 Web",
  news: "📰 Haber",
  social: "💬 Sosyal",
  academic: "🎓 Akademik/Resmi",
  media: "🖼️ Medya",
  custom: "🔧 Özel",
};

function ResearchBackendsTab() {
  const [rows, setRows] = useState<ResearchBackend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, ResearchBackendTestResult>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  async function loadRows() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListResearchBackends();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Listeleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRows();
  }, []);

  async function handleToggleEnabled(row: ResearchBackend) {
    try {
      await adminUpdateResearchBackend(row.id, { enabled: !row.enabled });
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Güncelleme hatası");
    }
  }

  async function handleTest(row: ResearchBackend) {
    setTesting(row.id);
    try {
      const res = await adminTestResearchBackend(row.id, "post-quantum");
      setTestResults((prev) => ({ ...prev, [row.id]: res }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [row.id]: {
          name: row.name,
          ok: false,
          elapsed_ms: 0,
          reason: err instanceof Error ? err.message : "Test hatası",
        },
      }));
    } finally {
      setTesting(null);
    }
  }

  async function handleDelete(row: ResearchBackend) {
    if (row.is_builtin) {
      alert("Yerleşik (builtin) kaynak silinemez. 'Aktif' anahtarını kapatın.");
      return;
    }
    if (!confirm(`'${row.display_name}' kaynağını kalıcı olarak silmek istediğinden emin misin?`)) return;
    try {
      await adminDeleteResearchBackend(row.id);
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Silme hatası");
    }
  }

  if (loading) return <p className="text-slate-400 text-sm">Yükleniyor…</p>;

  const enabledCount = rows.filter((r) => r.enabled).length;
  // İstihbarat Ajanı Faz 0 — category counts + filtered view. Unknown/
  // missing `category` (older cached responses) falls back to "custom"
  // so nothing silently disappears from the list.
  const categoryCounts = rows.reduce<Record<string, number>>((acc, r) => {
    const cat = r.category || "custom";
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const visibleRows = rows.filter(
    (r) => categoryFilter === "all" || (r.category || "custom") === categoryFilter
  );

  return (
    <section className="space-y-5">
      <header className="bg-gradient-to-r from-violet-500/10 via-fuchsia-500/5 to-transparent border border-violet-500/20 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1">
              Web Araştırma Kaynak Havuzu
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed max-w-3xl">
              Konsey her tartışmada bu kaynakların tümünü <span className="text-violet-300 font-medium">paralel</span> olarak sorgular,
              tekrarlayan URL&apos;leri eler ve domain itibarına göre yeniden sıralar.
              Bir kaynağı geçici olarak devre dışı bırakmak için <span className="text-violet-300">Aktif</span> anahtarını kapat.
              Kendi REST tabanlı arama API&apos;ni eklemek için <span className="text-violet-300">+ Özel Kaynak</span>.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-3xl font-bold text-violet-300">{enabledCount}</div>
            <div className="text-xs text-slate-400">aktif kaynak / {rows.length}</div>
          </div>
        </div>
      </header>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="px-4 py-2 text-sm bg-violet-600 hover:bg-violet-500 rounded-lg font-medium transition-colors"
        >
          {creating ? "İptal" : "+ Özel Kaynak"}
        </button>
      </div>

      {creating && (
        <ResearchBackendForm
          mode="create"
          onSaved={async () => {
            setCreating(false);
            await loadRows();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {/* İstihbarat Ajanı Faz 0 — kategori filtresi (WEB/NEWS/SOCIAL/ACADEMIC/MEDIA/CUSTOM) */}
      <div className="flex flex-wrap gap-2">
        {["all", ...Object.keys(RESEARCH_CATEGORY_LABELS).filter((c) => c !== "all")].map((cat) => {
          const count = cat === "all" ? rows.length : categoryCounts[cat] || 0;
          if (cat !== "all" && count === 0) return null;
          const active = categoryFilter === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                active
                  ? "bg-violet-600 border-violet-500 text-white"
                  : "bg-white/[0.02] border-white/10 text-slate-400 hover:border-white/20"
              }`}
            >
              {RESEARCH_CATEGORY_LABELS[cat] || cat} <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {visibleRows.map((row) => {
          const isEditing = editingId === row.id;
          const test = testResults[row.id];
          return (
            <div
              key={row.id}
              className={`bg-white/[0.02] border rounded-xl p-4 transition-colors ${
                row.enabled
                  ? "border-white/10 hover:border-white/20"
                  : "border-white/5 opacity-60"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-white">{row.display_name}</span>
                    <code className="text-[11px] text-slate-500 bg-slate-900/50 px-1.5 py-0.5 rounded">
                      {row.name}
                    </code>
                    {row.is_builtin ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30 uppercase tracking-wide">
                        builtin
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wide">
                        custom
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 border border-slate-500/30">
                      {RESEARCH_CATEGORY_LABELS[row.category || "custom"] || row.category}
                    </span>
                    {row.requires_key && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide ${
                          row.api_key_present
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                            : "bg-amber-500/15 text-amber-300 border-amber-500/30"
                        }`}
                        title={row.api_key_env || ""}
                      >
                        {row.api_key_present ? "anahtar ✓" : "anahtar yok"}
                      </span>
                    )}
                    {row.in_registry === false && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide bg-rose-500/15 text-rose-300 border-rose-500/40"
                        title="Kod düzeyinde devre dışı (env opt-out veya build'de yok). DB toggle'ı işe yaramaz."
                      >
                        env-opt-out
                      </span>
                    )}
                    {row.in_registry !== false &&
                      row.enabled &&
                      row.in_chain === false && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide bg-amber-500/15 text-amber-300 border-amber-500/30"
                          title="DB'de aktif ama overlay henüz yansımamış"
                        >
                          overlay bekliyor
                        </span>
                      )}
                    {row.in_registry !== false &&
                      row.enabled &&
                      row.in_chain === true && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                          title="Canlı arama zincirinde aktif"
                        >
                          canlı ✓
                        </span>
                      )}
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2">{row.description || "—"}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-500">
                    <span>öncelik: <span className="text-slate-300">{row.priority}</span></span>
                    <span>timeout: <span className="text-slate-300">{row.timeout_ms}ms</span></span>
                    <span>maks. sonuç: <span className="text-slate-300">{row.max_results}</span></span>
                    {row.api_key_env && (
                      <span>env: <code className="text-slate-300">{row.api_key_env}</code></span>
                    )}
                  </div>
                  {row.runtime_note && (
                    <div
                      className={`mt-2 px-3 py-2 rounded text-[11px] border flex items-start gap-2 ${
                        row.in_registry === false
                          ? "bg-rose-500/10 border-rose-500/30 text-rose-200"
                          : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                      }`}
                    >
                      <span aria-hidden>⚠</span>
                      <span>{row.runtime_note}</span>
                    </div>
                  )}
                  {test && (
                    <div
                      className={`mt-2 px-3 py-2 rounded text-xs border ${
                        test.ok && (test.count ?? 0) > 0
                          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                          : test.ok && test.warning
                            ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
                            : "bg-red-500/10 border-red-500/30 text-red-200"
                      }`}
                    >
                      {test.ok && (test.count ?? 0) > 0 ? (
                        <>✓ {test.count} sonuç · {test.elapsed_ms}ms</>
                      ) : test.ok && test.warning ? (
                        <>⚠ {test.warning} · {test.elapsed_ms}ms</>
                      ) : (
                        <>✗ {test.reason || "boş yanıt"} · {test.elapsed_ms}ms</>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={() => void handleToggleEnabled(row)}
                      className="w-4 h-4 accent-violet-500"
                    />
                    <span className="text-slate-300">Aktif</span>
                  </label>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => void handleTest(row)}
                      disabled={testing === row.id}
                      className="px-2.5 py-1 text-[11px] text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/60 hover:bg-cyan-500/10 rounded transition-colors disabled:opacity-40"
                    >
                      {testing === row.id ? "…" : "Test"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(isEditing ? null : row.id)}
                      className="px-2.5 py-1 text-[11px] text-slate-300 border border-white/10 hover:border-white/20 rounded transition-colors"
                    >
                      {isEditing ? "Kapat" : "Düzenle"}
                    </button>
                    {!row.is_builtin && (
                      <button
                        type="button"
                        onClick={() => void handleDelete(row)}
                        className="px-2.5 py-1 text-[11px] text-red-400 border border-red-500/30 hover:bg-red-500/10 rounded transition-colors"
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {isEditing && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <ResearchBackendForm
                    mode="edit"
                    initial={row}
                    onSaved={async () => {
                      setEditingId(null);
                      await loadRows();
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}


function ResearchBackendForm({
  mode,
  initial,
  onSaved,
  onCancel,
}: {
  mode: "create" | "edit";
  initial?: ResearchBackend;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    display_name: initial?.display_name ?? "",
    description: initial?.description ?? "",
    enabled: initial?.enabled ?? true,
    priority: initial?.priority ?? 100,
    timeout_ms: initial?.timeout_ms ?? 6000,
    max_results: initial?.max_results ?? 8,
    requires_key: initial?.requires_key ?? false,
    api_key_env: initial?.api_key_env ?? "",
    config_json: JSON.stringify(initial?.config ?? {}, null, 2),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig = form.config_json.trim() ? JSON.parse(form.config_json) : {};
    } catch {
      setError("Config alanı geçerli JSON değil.");
      setSaving(false);
      return;
    }
    try {
      if (isEdit && initial) {
        const patch: ResearchBackendPatchPayload = {
          display_name: form.display_name,
          description: form.description,
          enabled: form.enabled,
          priority: form.priority,
          timeout_ms: form.timeout_ms,
          max_results: form.max_results,
          requires_key: form.requires_key,
          api_key_env: form.api_key_env || null,
          config: parsedConfig,
        };
        await adminUpdateResearchBackend(initial.id, patch);
      } else {
        const payload: ResearchBackendCreatePayload = {
          name: form.name,
          display_name: form.display_name,
          description: form.description,
          enabled: form.enabled,
          priority: form.priority,
          timeout_ms: form.timeout_ms,
          max_results: form.max_results,
          requires_key: form.requires_key,
          api_key_env: form.api_key_env || null,
          config: parsedConfig,
        };
        await adminCreateResearchBackend(payload);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kayıt hatası");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 text-sm">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Sistem Adı (slug)"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v }))}
          disabled={isEdit}
          placeholder="ornek_kaynak"
          hint="küçük harf, rakam, _-"
        />
        <Input
          label="Görünen Ad"
          value={form.display_name}
          onChange={(v) => setForm((f) => ({ ...f, display_name: v }))}
          placeholder="Örnek Kaynak"
        />
      </div>
      <Input
        label="Açıklama"
        value={form.description}
        onChange={(v) => setForm((f) => ({ ...f, description: v }))}
        placeholder="Bu kaynak ne için iyi?"
      />
      <div className="grid grid-cols-3 gap-3">
        <NumInput
          label="Öncelik"
          value={form.priority}
          onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
          hint="büyük = daha üstte"
        />
        <NumInput
          label="Timeout (ms)"
          value={form.timeout_ms}
          onChange={(v) => setForm((f) => ({ ...f, timeout_ms: v }))}
          hint="500–60000"
        />
        <NumInput
          label="Maks. Sonuç"
          value={form.max_results}
          onChange={(v) => setForm((f) => ({ ...f, max_results: v }))}
          hint="1–50"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <Input
          label="API Anahtar Env Değişkeni"
          value={form.api_key_env}
          onChange={(v) => setForm((f) => ({ ...f, api_key_env: v }))}
          placeholder="ÖRNEK: BRAVE_SEARCH_API_KEY"
          hint="boş bırak: anahtar gerekmez"
        />
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 mb-2 ml-1">
          <input
            type="checkbox"
            checked={form.requires_key}
            onChange={(e) => setForm((f) => ({ ...f, requires_key: e.target.checked }))}
            className="w-4 h-4 accent-violet-500"
          />
          API anahtarı zorunlu
        </label>
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">
          Özel Yapılandırma (JSON)
        </label>
        <textarea
          value={form.config_json}
          onChange={(e) => setForm((f) => ({ ...f, config_json: e.target.value }))}
          rows={6}
          className="w-full px-3 py-2 bg-slate-950/50 border border-white/10 rounded-lg text-xs text-cyan-200 font-mono focus:outline-none focus:ring-1 focus:ring-violet-500"
          placeholder='{"endpoint": "https://api.example.com/search", "query_param": "q"}'
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Yerleşik kaynaklar için bu alan bilgi amaçlıdır; özel kaynaklarda generic JSON backend tarafından kullanılır.
        </p>
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-slate-300">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          className="w-4 h-4 accent-violet-500"
        />
        Aktif
      </label>
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm bg-violet-600 hover:bg-violet-500 rounded-lg font-medium disabled:opacity-40 transition-colors"
        >
          {saving ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
        >
          İptal
        </button>
      </div>
    </form>
  );
}


function Input({
  label,
  value,
  onChange,
  placeholder,
  hint,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50"
      />
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}


function NumInput({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
      />
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
